//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import { Box, Paper, Tab } from '@mui/material'
import { PoolLifeCycleState, PoolTabEnum, PoolTypeEnum, RoleEnum } from '@shared/Enums'
import { EthAddr } from '@shared/SchemaTypes'
import { PoolDynamicOverviewCTO, PoolFlexOverviewCTO, PoolOverviewCTO } from '@shared/sharedTypes'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import React, { ReactElement, useEffect } from 'react'

import { useNavigate, useParams } from 'react-router-dom'
import BorrowerAdminPoolActionModals from '@react/borrower/BorrowerAdminPoolActionModals'
import LoanList from '@react/components/loans/LoanList'
import PoolAssets from '@react/components/pools/PoolAssets'
import PoolContractAddresses from '@react/components/pools/PoolContractAddresses'
import PoolLegalPage from '@react/components/pools/PoolLegalPage'
import PoolSummary from '@react/components/pools/PoolSummary'
import PoolOverview from '@react/lender/PoolOverview'
import { useOtSelector } from '@react/redux/store'
import AdminGrantPoolAccessPage from './AdminGrantPoolAccessPage'
import { AdminLenderView } from './AdminLenderView'
import AdminPoolActivationChecklist from './AdminPoolActivationChecklist'
import { AdminWithdrawalFlexTab } from './AdminWithdrawalsFlexTab'
import { PoolOffchainDataView } from './PoolOffchainDataView'
import { BorrowerWithdrawalTab } from '@react/borrower/BorrowerWithdrawalsTab'
import { PoolFlexCalendar } from '@react/components/pools/PoolFlexCalendat'

import DynamicBorrowerAdminPoolActionModals from '@react/dynamic/DynamicBorrowerAdminPoolActionModals'
import { AdminDynamicWithdrawalsTab } from '@react/dynamic/AdminDynamicWithdrawalsTab'
import { DynamicDepositsTab } from '@react/dynamic/DynamicDepositsTab'
import { logOT } from '@shared/loggingShared'

export default function AdminPoolDetails(props: { poolAddr?: EthAddr }): ReactElement {
  const p = useParams().poolAddr
  const poolAddr = (props.poolAddr ? props.poolAddr : p) as EthAddr

  const navigate = useNavigate()
  const chainState = useOtSelector((state) => state.chainState)
  const roleEnum = chainState.userConnection?.roleEnum
  const poolBaseOverviewCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO])

  const allPools = useOtSelector((state) => state.poolState.poolOverviewCTOs)
  const poolOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.Term ? (poolBaseOverviewCTO as PoolOverviewCTO) : null
  const poolFlexOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.FlexibleRate ? (poolBaseOverviewCTO as PoolFlexOverviewCTO) : null
  const poolDynamicOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.Dynamic ? (poolBaseOverviewCTO as PoolDynamicOverviewCTO) : null
  const [selectedTab, setSelectedTab] = React.useState<PoolTabEnum>(PoolTabEnum.OVERVIEW)
  const handleChange = (_event: React.SyntheticEvent, newValue: PoolTabEnum): void => {
    setSelectedTab(newValue)
  }
  useEffect(() => {
    if (poolAddr && !poolBaseOverviewCTO && Object.keys(allPools).length > 0) {
      logOT.info('location navigate to /')
      navigate('/')
    }
  }, [poolAddr, poolBaseOverviewCTO, allPools])
  if (
    roleEnum === RoleEnum.POOL_ADMIN &&
    (poolOverviewCTO?.state === PoolLifeCycleState.Initialized ||
      poolFlexOverviewCTO?.state === PoolLifeCycleState.Initialized)
  ) {
    return <AdminPoolActivationChecklist poolAddr={poolAddr} />
  }
  logOT.debug('AdminPoolDetails view ', roleEnum, roleEnum === RoleEnum.POOL_ADMIN || roleEnum === RoleEnum.BORROWER)

  if (poolBaseOverviewCTO) {
    return (
      <Box>
        {poolDynamicOverviewCTO && (
          <DynamicBorrowerAdminPoolActionModals poolAddr={poolAddr} poolName={poolBaseOverviewCTO.name} />
        )}
        {!poolDynamicOverviewCTO && (
          <BorrowerAdminPoolActionModals poolName={poolBaseOverviewCTO.name} poolAddr={poolAddr} />
        )}

        <PoolSummary poolAddr={poolAddr} />
        <Paper sx={{ marginTop: 4, marginBottom: 4, borderRadius: 4 }}>
          <Box px={3} py={2}>
            <TabContext value={selectedTab}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList
                  onChange={handleChange}
                  aria-label="lab API tabs example"
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label={'Overview'} value={PoolTabEnum.OVERVIEW} />
                  {/* <Tab label={'Schedule'} value={PoolTabEnum.SCHEDULE} /> */}
                  {poolOverviewCTO && <Tab label={'Loans'} value={PoolTabEnum.LOANS} />}
                  {poolDynamicOverviewCTO && <Tab label={'Deposits'} value={PoolTabEnum.DEPOSITS} />}
                  <Tab label={'Withdrawals'} value={PoolTabEnum.WITHDRAWS} />
                  {(roleEnum === RoleEnum.POOL_ADMIN || roleEnum === RoleEnum.BORROWER) && (
                    <Tab label={'Lender View'} value={PoolTabEnum.LENDER_VIEW} />
                  )}
                  <Tab label={'OffChainData'} value={PoolTabEnum.OFFCHAIN_DATA} />
                  <Tab label={'Access List'} value={PoolTabEnum.ACCESS_LIST} />
                  <Tab label={'Info'} value={PoolTabEnum.LEGAL} />
                  <Tab label={'Collateral'} value={PoolTabEnum.ASSETS} />
                  <Tab label={'Contract Addresses'} value={PoolTabEnum.CONTRACT_ADDRESSES} />
                  {poolBaseOverviewCTO?.poolType === PoolTypeEnum.FlexibleRate && (
                    <Tab label={'Calendar'} value={PoolTabEnum.FLEX_CALENDAR} />
                  )}
                </TabList>
              </Box>
              <TabPanel value={PoolTabEnum.OVERVIEW}>
                <PoolOverview />
              </TabPanel>
              {/* <TabPanel value={PoolTabEnum.SCHEDULE}><PoolSchedule/></TabPanel> */}
              {poolOverviewCTO && (
                <TabPanel value={PoolTabEnum.LOANS}>
                  <LoanList />
                </TabPanel>
              )}
              {poolDynamicOverviewCTO && (
                <TabPanel value={PoolTabEnum.DEPOSITS}>
                  <DynamicDepositsTab poolAddr={poolAddr} />
                </TabPanel>
              )}
              {poolOverviewCTO && (
                <TabPanel value={PoolTabEnum.WITHDRAWS}>
                  <BorrowerWithdrawalTab poolAddr={poolAddr}></BorrowerWithdrawalTab>
                </TabPanel>
              )}

              {poolFlexOverviewCTO && (
                <TabPanel value={PoolTabEnum.WITHDRAWS}>
                  <AdminWithdrawalFlexTab poolAddr={poolAddr} />
                </TabPanel>
              )}
              {poolDynamicOverviewCTO && (
                <TabPanel value={PoolTabEnum.WITHDRAWS}>
                  <AdminDynamicWithdrawalsTab poolAddr={poolAddr} />
                </TabPanel>
              )}
              {(roleEnum === RoleEnum.POOL_ADMIN || roleEnum === RoleEnum.BORROWER) && (
                <TabPanel value={PoolTabEnum.LENDER_VIEW}>
                  <AdminLenderView poolAddr={poolAddr} />
                </TabPanel>
              )}

              <TabPanel value={PoolTabEnum.ACCESS_LIST}>
                <AdminGrantPoolAccessPage poolAddr={poolAddr} />
              </TabPanel>
              <TabPanel value={PoolTabEnum.LEGAL}>
                <PoolLegalPage />
              </TabPanel>
              <TabPanel value={PoolTabEnum.OFFCHAIN_DATA}>
                <PoolOffchainDataView poolAddr={poolAddr} />
              </TabPanel>

              <TabPanel value={PoolTabEnum.ASSETS}>
                <PoolAssets />
              </TabPanel>
              <TabPanel value={PoolTabEnum.CONTRACT_ADDRESSES}>
                <PoolContractAddresses role={RoleEnum.POOL_ADMIN} />
              </TabPanel>
              <TabPanel value={PoolTabEnum.FLEX_CALENDAR}>
                <PoolFlexCalendar poolAddr={poolAddr} role={RoleEnum.POOL_ADMIN} />
              </TabPanel>
            </TabContext>
          </Box>
        </Paper>
      </Box>
    )
  } else {
    return <></>
  }
}
